import { useState, useEffect, useRef, useMemo } from "react";
import {
  Button,
  Modal,
  Iconfont,
  NumberInputWithArrows,
} from "@eweadn-qiankun/shared-components";
import {
  keyAxisImage01,
  keyAxisImage02,
  keyAxisImage03,
} from "@eweadn-qiankun/shared-assets";
import "./SettingTravelModal.scss";
import { useTranslation } from "react-i18next";

interface SettingTravelModalProps {
  showTravelModal: boolean;
  checkData: any;
  getAfterChangeData: (data: any) => void;
  onClose: () => void;
  activeDbs?: 0 | 1 | 2 | 3;
  /** 华奋达磁轴：四个独立行程值 */
  isMagnetic?: boolean;
  keyboardType?: string;
}

import {
  HFD_MAGNETIC_DKS_DEFAULT_DBS,
  resolveMagneticDksDefaultDbs,
} from "./originalData";
import { resolveHfdMagneticTravelRangeMm } from "@eweadn-qiankun/shared-utils";

const DEFAULT_MIN = 0.1;
const DEFAULT_MAX = 3.3;
const STEP = 0.01;
const DEFAULT_D0 = 1.4;
const DEFAULT_D1 = 3.0;
/** 星闪双点默认；磁轴用 HFD_MAGNETIC_DKS_DEFAULT_DBS */
const DEFAULT_MAGNETIC_DBS = HFD_MAGNETIC_DKS_DEFAULT_DBS;

const MAGNETIC_INPUT_META = [
  {
    tipClass: "press",
    labelKey: "keyboard.advancedKey.dksModes.start",
  },
  {
    tipClass: "bottomOut",
    labelKey: "keyboard.advancedKey.dksModes.pressBottom",
  },
  {
    tipClass: "bottomOut",
    labelKey: "keyboard.advancedKey.dksModes.releaseBottom",
  },
  {
    tipClass: "reset",
    labelKey: "keyboard.advancedKey.dksModes.reset",
  },
] as const;

/** 垂直滑轨：值越小越靠上(bottom越大)，值越大越靠下(bottom越小) */
const valueToPercent = (v: number, min: number, max: number) =>
  ((max - v) / (max - min)) * 100;
const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

const SettingTravelModal = ({
  showTravelModal,
  checkData,
  getAfterChangeData,
  onClose,
  activeDbs = 0,
  isMagnetic = false,
  keyboardType,
}: SettingTravelModalProps) => {
  const magneticDefaults = useMemo(
    () => [...resolveMagneticDksDefaultDbs(keyboardType)],
    [keyboardType]
  );
  const travelRange = useMemo(() => {
    if (isMagnetic) return resolveHfdMagneticTravelRangeMm(keyboardType);
    return { min: DEFAULT_MIN, max: DEFAULT_MAX };
  }, [isMagnetic, keyboardType]);
  const { min: travelMin, max: travelMax } = travelRange;
  const { t } = useTranslation();
  const [dbs0, setDbs0] = useState(DEFAULT_D0);
  const [dbs1, setDbs1] = useState(DEFAULT_D1);
  const [magneticDbs, setMagneticDbs] = useState<number[]>([
    ...DEFAULT_MAGNETIC_DBS,
  ]);
  const [dragging, setDragging] = useState<"dbs0" | "dbs1" | null>(null);
  const [openCount, setOpenCount] = useState(0);

  const [input0, setInput0] = useState<string | null>(null);
  const [input1, setInput1] = useState<string | null>(null);
  const [magneticInputs, setMagneticInputs] = useState<Array<string | null>>([
    null,
    null,
    null,
    null,
  ]);
  const [focusedInput, setFocusedInput] = useState<
    "dbs0" | "dbs1" | 0 | 1 | 2 | 3 | null
  >(null);

  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<"dbs0" | "dbs1" | null>(null);
  const activeAxisRef = useRef<"dbs0" | "dbs1">("dbs0");
  const dbs0Ref = useRef(DEFAULT_D0);
  const dbs1Ref = useRef(DEFAULT_D1);
  const magneticDbsRef = useRef<number[]>([...DEFAULT_MAGNETIC_DBS]);
  const checkDataRef = useRef(checkData);
  const getAfterChangeDataRef = useRef(getAfterChangeData);
  const initialValuesRef = useRef({ d0: DEFAULT_D0, d1: DEFAULT_D1 });
  const initialMagneticRef = useRef<number[]>([...DEFAULT_MAGNETIC_DBS]);

  dbs0Ref.current = dbs0;
  dbs1Ref.current = dbs1;
  magneticDbsRef.current = magneticDbs;
  checkDataRef.current = checkData;
  getAfterChangeDataRef.current = getAfterChangeData;

  // Sync from checkData when modal opens, but clamp to valid range
  useEffect(() => {
    initialValuesRef.current = { d0: DEFAULT_D0, d1: DEFAULT_D1 };
    initialMagneticRef.current = [...magneticDefaults];
    if (showTravelModal && isMagnetic) {
      const src = Array.isArray(checkData?.otherData?.dbs)
        ? checkData.otherData.dbs
        : [];
      const next =
        src.length < 4
          ? [...magneticDefaults]
          : magneticDefaults.map((fb, i) => {
              const raw = Number(src[i]);
              return raw >= travelMin && raw <= travelMax ? raw : fb;
            });
      setMagneticDbs(next);
      magneticDbsRef.current = next;
      initialMagneticRef.current = [...magneticDefaults];
    } else if (showTravelModal && checkData?.otherData?.dbs) {
      const raw0 = checkData.otherData.dbs[0];
      const raw1 = checkData.otherData.dbs[1];
      const d0 =
        raw0 != null && raw0 >= travelMin && raw0 <= travelMax
          ? raw0
          : DEFAULT_D0;
      const d1 =
        raw1 != null && raw1 >= travelMin && raw1 <= travelMax
          ? raw1
          : DEFAULT_D1;
      const lo = Math.min(d0, d1);
      const hi = Math.max(d0, d1);
      setDbs0(lo);
      setDbs1(hi);
    } else if (showTravelModal) {
      setDbs0(DEFAULT_D0);
      setDbs1(DEFAULT_D1);
      setMagneticDbs([...magneticDefaults]);
    }
    if (showTravelModal) {
      setOpenCount((c) => c + 1);
    }
    setInput0(null);
    setInput1(null);
    setMagneticInputs([null, null, null, null]);
    setFocusedInput(null);
    activeAxisRef.current = activeDbs === 1 ? "dbs1" : "dbs0";
  }, [
    showTravelModal,
    activeDbs,
    isMagnetic,
    checkData,
    keyboardType,
    travelMin,
    travelMax,
    magneticDefaults,
  ]);

  const saveDbs = (d0: number, d1: number) => {
    const cd = checkDataRef.current;
    const fn = getAfterChangeDataRef.current;
    if (!cd || !fn) return;
    const newCheckData = JSON.parse(JSON.stringify(cd));
    if (!newCheckData?.otherData?.dbs) return;
    newCheckData.otherData.dbs[0] = d0;
    newCheckData.otherData.dbs[1] = d1;
    fn(newCheckData);
  };

  const saveMagneticDbs = (values: number[]) => {
    const cd = checkDataRef.current;
    const fn = getAfterChangeDataRef.current;
    if (!cd || !fn) return;
    const newCheckData = JSON.parse(JSON.stringify(cd));
    if (!newCheckData.otherData) newCheckData.otherData = {};
    newCheckData.otherData.dbs = values.map((v) =>
      clamp(Math.round(v / STEP) * STEP, travelMin, travelMax)
    );
    fn(newCheckData);
  };

  const handleMagneticChange = (index: 0 | 1 | 2 | 3, value: string) => {
    setMagneticInputs((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    const v = Number(value);
    if (isNaN(v) || value === "" || value.endsWith(".")) return;
    const stepped = Math.round(v / STEP) * STEP;
    const clamped = clamp(stepped, travelMin, travelMax);
    const next = [...magneticDbsRef.current];
    next[index] = clamped;
    setMagneticDbs(next);
    magneticDbsRef.current = next;
    setMagneticInputs((prev) => {
      const cleared = [...prev];
      cleared[index] = null;
      return cleared;
    });
    saveMagneticDbs(next);
  };

  const updateFromTrack = (clientY: number) => {
    const track = trackRef.current;
    if (!track || !draggingRef.current) return;
    const rect = track.getBoundingClientRect();
    if (rect.height === 0) return;
    const pct = clamp((clientY - rect.top) / rect.height, 0, 1);
    // 鼠标越往下 → pct越大 → 值越大(MAX)
    const raw = travelMin + pct * (travelMax - travelMin);
    const stepped = Math.round(raw / STEP) * STEP;
    const v = clamp(stepped, travelMin, travelMax);

    if (draggingRef.current === "dbs0") {
      const d1 = dbs1Ref.current;
      const clamped = Math.min(v, d1);
      setDbs0(clamped);
      saveDbs(clamped, d1);
    } else {
      const d0 = dbs0Ref.current;
      const clamped = Math.max(v, d0);
      setDbs1(clamped);
      saveDbs(d0, clamped);
    }
  };

  const handleTrackMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    if (rect.height === 0) return;
    const pct = clamp((e.clientY - rect.top) / rect.height, 0, 1);
    // 鼠标越往下 → pct越大 → 值越大(MAX)
    const raw = travelMin + pct * (travelMax - travelMin);
    const stepped = Math.round(raw / STEP) * STEP;
    const clickValue = clamp(stepped, travelMin, travelMax);

    const dist0 = Math.abs(clickValue - dbs0Ref.current);
    const dist1 = Math.abs(clickValue - dbs1Ref.current);
    draggingRef.current = dist0 <= dist1 ? "dbs0" : "dbs1";
    activeAxisRef.current = draggingRef.current;
    setDragging(draggingRef.current);
    setFocusedInput(draggingRef.current);

    if (draggingRef.current === "dbs0") {
      const v = Math.min(clickValue, dbs1Ref.current);
      setDbs0(v);
      saveDbs(v, dbs1Ref.current);
    } else {
      const v = Math.max(clickValue, dbs0Ref.current);
      setDbs1(v);
      saveDbs(dbs0Ref.current, v);
    }
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      e.preventDefault();
      updateFromTrack(e.clientY);
    };
    const handleMouseUp = () => {
      if (draggingRef.current) {
        draggingRef.current = null;
        setDragging(null);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      }
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  // ==================== 输入框 ====================

  const handleDbs0Change = (value: string) => {
    setInput0(value);
    const v = Number(value);
    if (isNaN(v) || value === "" || value.endsWith(".")) return;
    const stepped = Math.round(v / STEP) * STEP;
    const clamped = clamp(stepped, travelMin, dbs1Ref.current);
    activeAxisRef.current = "dbs0";
    setDbs0(clamped);
    setInput0(null);
    saveDbs(clamped, dbs1Ref.current);
  };

  const handleDbs1Change = (value: string) => {
    setInput1(value);
    const v = Number(value);
    if (isNaN(v) || value === "" || value.endsWith(".")) return;
    const stepped = Math.round(v / STEP) * STEP;
    const clamped = clamp(stepped, dbs0Ref.current, travelMax);
    activeAxisRef.current = "dbs1";
    setDbs1(clamped);
    setInput1(null);
    saveDbs(dbs0Ref.current, clamped);
  };

  const handleReset = () => {
    if (isMagnetic) {
      const next = [...initialMagneticRef.current];
      setMagneticDbs(next);
      magneticDbsRef.current = next;
      setMagneticInputs([null, null, null, null]);
      saveMagneticDbs(next);
      return;
    }
    const { d0, d1 } = initialValuesRef.current;
    setDbs0(d0);
    setDbs1(d1);
    setInput0(null);
    setInput1(null);
    saveDbs(d0, d1);
  };

  // ==================== 轴体位置 ====================

  const getAxisPosition = (value: number) => (value / travelMax) * 30;

  // ==================== 渲染 ====================

  const activeMagneticValue =
    magneticDbs[Number(activeDbs)] ?? magneticDefaults[Number(activeDbs)];
  const pct0 = valueToPercent(
    isMagnetic ? activeMagneticValue : dbs0,
    travelMin,
    travelMax
  );
  const pct1 = valueToPercent(dbs1, travelMin, travelMax);
  const isDragging = dragging !== null;

  const scaleMarks = Array.from({ length: 33 }, (_, i) => {
    const pos = ((32 - i) / 32) * 100;
    const isMain = i % 4 === 0 || i === 0 || i === 32;
    const val = travelMin + (i / 32) * (travelMax - travelMin);
    return { pos, isMain, val };
  });

  return (
    <Modal
      visible={showTravelModal}
      title={t("keyboard.advancedKey.dksModes.setTravel")}
      footer={false}
      onClose={onClose}
      width="auto"
    >
      <div
        className={`setting-travel-container ${isDragging ? "setting-travel-dragging" : ""}`}
      >
        {/* ===== 左侧：轴体图 ===== */}
        <div className="setting-travel-axis-section">
          <div className="setting-travel-axis-image-box">
            <img
              src={keyAxisImage01}
              alt=""
              draggable={false}
              className="setting-travel-axis-base"
            />
            <div
              className="setting-travel-axis-slider-layer"
              style={{ zIndex: 2 }}
            >
              <img
                src={keyAxisImage02}
                alt=""
                draggable={false}
                className="setting-travel-axis-slider-img"
                style={{
                  transform: `translateY(${getAxisPosition(
                    isMagnetic
                      ? activeMagneticValue
                      : activeAxisRef.current === "dbs1"
                        ? dbs1
                        : dbs0
                  )}%)`,
                }}
              />
            </div>
            <img
              src={keyAxisImage03}
              alt=""
              draggable={false}
              className="setting-travel-axis-shell"
            />
          </div>
          <div className="setting-travel-axis-tips">
            {t("keyboard.advancedKey.dksModes.travelTips")}
          </div>
        </div>

        {/* ===== 中部：垂直滑轨 ===== */}
        <div className="setting-travel-track-section">
          <div className="setting-travel-track-outer">
            <div className="setting-travel-scale-col">
              {scaleMarks.map((m, i) => (
                <div
                  key={i}
                  className={`setting-travel-scale-mark ${m.isMain ? "setting-travel-scale-main" : ""}`}
                  style={{ bottom: `${m.pos}%` }}
                >
                  {m.isMain && (
                    <span className="setting-travel-scale-value">
                      {m.val.toFixed(1)}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div className="setting-travel-track-wrap">
              <div
                ref={trackRef}
                className="setting-travel-track"
                onMouseDown={isMagnetic ? undefined : handleTrackMouseDown}
              >
                {isMagnetic ? (
                  magneticDbs.map((v, i) => (
                    <div
                      key={i}
                      className={`setting-travel-thumb-line ${
                        Number(activeDbs) === i
                          ? "setting-travel-thumb-active"
                          : ""
                      }`}
                      style={{
                        bottom: `${valueToPercent(v, travelMin, travelMax)}%`,
                      }}
                    />
                  ))
                ) : (
                  <>
                    <div
                      className="setting-travel-range-fill"
                      style={{
                        bottom: `${pct1}%`,
                        height: `${pct0 - pct1}%`,
                      }}
                    />
                    <div
                      className={`setting-travel-thumb-line ${dragging === "dbs0" ? "setting-travel-thumb-active" : ""}`}
                      style={{ bottom: `${pct0}%` }}
                    />
                    <div
                      className={`setting-travel-thumb-line ${dragging === "dbs1" ? "setting-travel-thumb-active" : ""}`}
                      style={{ bottom: `${pct1}%` }}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
          <Button
            variant="default"
            leftIcon={
              <Iconfont icon="reset" className="setting-travel-reset-icon" />
            }
            onClick={handleReset}
          >
            {t("keyboard.button.reset")}
          </Button>
        </div>

        {/* ===== 右侧：数值输入 ===== */}
        <div className="setting-travel-input-section">
          <div className="setting-travel-input-header">
            <div className="travel-input-header-title">
              <span>
                {t("keyboard.advancedKey.dksModes.settingTitle.text1")}
              </span>
              {isMagnetic ? (
                <span>
                  （
                  {t(
                    MAGNETIC_INPUT_META[Number(activeDbs) as 0 | 1 | 2 | 3]
                      .labelKey
                  )}
                  ）
                </span>
              ) : (
                <span>
                  {(dragging ?? activeAxisRef.current) === "dbs0"
                    ? `（${t("keyboard.advancedKey.dksModes.start")} / ${t("keyboard.advancedKey.dksModes.reset")}）`
                    : `（${t("keyboard.advancedKey.dksModes.bottom")}）`}
                </span>
              )}
            </div>
            <div className="travel-input-header-title">
              {t("keyboard.advancedKey.dksModes.settingTitle.text2")}
            </div>
          </div>
          {isMagnetic ? (
            <div className="setting-travel-input-row setting-travel-input-row--magnetic">
              {MAGNETIC_INPUT_META.map((meta, index) => {
                const idx = index as 0 | 1 | 2 | 3;
                const draft = magneticInputs[idx];
                const value =
                  draft !== null
                    ? draft
                    : Number(magneticDbs[idx] ?? magneticDefaults[idx]).toFixed(
                        2
                      );
                return (
                  <div
                    className={`setting-travel-input-item ${meta.tipClass}`}
                    key={meta.labelKey}
                  >
                    <div className="label">
                      <div className="icon"></div>
                      <div className="text">{t(meta.labelKey)}</div>
                    </div>
                    <NumberInputWithArrows
                      key={`dbs${idx}-${openCount}`}
                      value={value}
                      onChange={(v) => handleMagneticChange(idx, v)}
                      min={travelMin}
                      max={travelMax}
                      step={STEP}
                      unit="mm"
                      decimal
                      showArrows={false}
                      className={
                        focusedInput === idx || Number(activeDbs) === idx
                          ? "setting-travel-input-active"
                          : ""
                      }
                      onFocus={() => setFocusedInput(idx)}
                      onBlur={() => setFocusedInput(null)}
                      autoFocus={Number(activeDbs) === idx}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="setting-travel-input-row">
              <div className="setting-travel-input-item press">
                <div className="label">
                  <div className="icon"></div>
                  <div className="text">
                    {t("keyboard.advancedKey.dksModes.start")}
                  </div>
                </div>
                <NumberInputWithArrows
                  key={`dbs0-${openCount}`}
                  value={input0 !== null ? input0 : dbs0.toFixed(2)}
                  onChange={handleDbs0Change}
                  min={travelMin}
                  max={dbs1}
                  step={STEP}
                  unit="mm"
                  decimal
                  showArrows={false}
                  className={
                    focusedInput === "dbs0" ? "setting-travel-input-active" : ""
                  }
                  onFocus={() => setFocusedInput("dbs0")}
                  onBlur={() => setFocusedInput(null)}
                  autoFocus={activeDbs === 0}
                />
              </div>
              <div className="setting-travel-input-item bottomOut">
                <div className="label">
                  <div className="icon"></div>
                  <div className="text">
                    {t("keyboard.advancedKey.dksModes.bottom")}
                  </div>
                </div>
                <NumberInputWithArrows
                  key={`dbs1-${openCount}`}
                  value={input1 !== null ? input1 : dbs1.toFixed(2)}
                  onChange={handleDbs1Change}
                  min={dbs0}
                  max={travelMax}
                  step={STEP}
                  unit="mm"
                  decimal
                  showArrows={false}
                  className={
                    focusedInput === "dbs1" ? "setting-travel-input-active" : ""
                  }
                  onFocus={() => setFocusedInput("dbs1")}
                  onBlur={() => setFocusedInput(null)}
                  autoFocus={activeDbs === 1}
                />
              </div>
              <div className="setting-travel-input-item reset">
                <div className="label">
                  <div className="icon"></div>
                  <div className="text">
                    {t("keyboard.advancedKey.dksModes.reset")}
                  </div>
                </div>
                <NumberInputWithArrows
                  value={input0 !== null ? input0 : dbs0.toFixed(2)}
                  onChange={handleDbs0Change}
                  min={travelMin}
                  max={travelMax}
                  step={STEP}
                  unit="mm"
                  decimal
                  showArrows={false}
                  className={
                    focusedInput === "dbs0" ? "setting-travel-input-active" : ""
                  }
                  onFocus={() => setFocusedInput("dbs0")}
                  onBlur={() => setFocusedInput(null)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
export default SettingTravelModal;
